import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Capability } from '@mpr/shared';
import { useEffect, useState } from 'react';

import {
  api,
  ApiError,
  type SocialAppRow,
  type SocialConnectionRow,
  type SocialProviderId,
  type SocialProviderInfo,
} from '../lib/api.js';
import { networkLabel } from '../lib/channels.js';
import { SocialNetworksAdmin } from './SocialNetworksAdmin.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { Icon } from './Icons.js';

type Notice = { kind: 'ok' | 'error'; text: string } | null;

/**
 * Social accounts other than Meta: connecting them, and the app credentials
 * that make connecting possible.
 *
 * Provider-neutral on purpose. Everything provider-specific arrives from the
 * server (the list of providers, whether each is set up), so a second network is
 * a server change and a translation key, not another panel.
 */
export function SocialConnectionsPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [notice, setNotice] = useState<Notice>(null);

  const data = useQuery({ queryKey: ['social-connections'], queryFn: api.socialConnections });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const mayManage = (me.data?.capabilities ?? []).includes(Capability.SETTINGS_WRITE);

  const providerName = (provider: SocialProviderId) =>
    networkLabel(
      t,
      provider,
      data.data?.providers.find((entry) => entry.provider === provider)?.displayName,
    );

  // The provider's callback redirects back here with the outcome in the query.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('provider') as SocialProviderId | null;
    if (params.get('social') === 'connected' && provider) {
      setNotice({
        kind: 'ok',
        text: t('social.connected', {
          provider: networkLabel(t, provider),
          count: params.get('accounts') ?? 0,
        }),
      });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('socialError')) {
      setNotice({
        kind: 'error',
        text: params.get('socialDetail') ?? params.get('socialError') ?? t('common.requestFailed'),
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['social-connections'] });
    void queryClient.invalidateQueries({ queryKey: ['social-apps'] });
  };

  const fail = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  /** The token network whose places are being named, when its form is open. */
  const [directFor, setDirectFor] = useState<SocialProviderInfo | null>(null);
  const [places, setPlaces] = useState('');

  const direct = useMutation({
    mutationFn: api.connectSocialDirect,
    onSuccess: (result) => {
      setNotice({
        kind: 'ok',
        text: t('social.connected', {
          provider: providerName(directFor?.provider ?? ''),
          count: result.accountsDiscovered,
        }),
      });
      setDirectFor(null);
      setPlaces('');
      refresh();
    },
    onError: fail,
  });

  const begin = (provider: SocialProviderId) => {
    const info = data.data?.providers.find((entry) => entry.provider === provider);
    if (info?.authType === 'token') setDirectFor(info);
    else connect.mutate(provider);
  };

  const connect = useMutation({
    mutationFn: api.startSocialOAuth,
    onSuccess: (result) => {
      window.location.href = result.authorizationUrl;
    },
    onError: fail,
  });

  const verify = useMutation({
    mutationFn: api.verifySocialConnection,
    onSuccess: (result) => {
      setNotice({
        kind: result.valid ? 'ok' : 'error',
        text: result.valid ? t('connections.tokenValid') : (result.reason ?? t('common.requestFailed')),
      });
      refresh();
    },
    onError: fail,
  });

  const disconnect = useMutation({
    mutationFn: api.disconnectSocial,
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('connections.disconnected') });
      refresh();
    },
    onError: fail,
  });

  if (data.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (data.isError || !data.data) return <div className="notice error">{t('common.requestFailed')}</div>;

  const { connections, providers } = data.data;

  return (
    <section className="panel" style={{ marginTop: 26 }}>
      <div className="panel-head">
        <h2>{t('social.title')}</h2>
        <div className="actions">
          {providers.map((entry) => (
            <button
              key={entry.provider}
              type="button"
              className="primary"
              onClick={() => begin(entry.provider)}
              disabled={!mayManage || !entry.configured || connect.isPending}
            >
              <Icon.plus />
              {connections.some((row) => row.provider === entry.provider)
                ? t('social.connectAnother', { provider: providerName(entry.provider) })
                : t('social.connect', { provider: providerName(entry.provider) })}
            </button>
          ))}
        </div>
      </div>
      <p className="tiny muted">{t('social.hint')}</p>

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {directFor && (
        <form
          className="card composer"
          onSubmit={(event) => {
            event.preventDefault();
            const accountIds = places
              .split(/[\n,]+/)
              .map((entry) => entry.trim())
              .filter((entry) => entry.length > 0);
            if (accountIds.length > 0) direct.mutate({ provider: directFor.provider, accountIds });
          }}
        >
          <h4>{t('social.connect', { provider: providerName(directFor.provider) })}</h4>
          <label htmlFor="social-direct-places">{directFor.accountsLabel}</label>
          <textarea
            id="social-direct-places"
            className="ltr"
            rows={3}
            value={places}
            onChange={(event) => setPlaces(event.target.value)}
            placeholder="@my_channel"
          />
          <p className="tiny muted">{t('social.direct.hint')}</p>
          <div className="actions">
            <button type="submit" className="primary" disabled={direct.isPending || !places.trim()}>
              {t('social.direct.submit')}
            </button>
            <button type="button" onClick={() => setDirectFor(null)} disabled={direct.isPending}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {providers
        .filter((entry) => !entry.configured)
        .map((entry) => (
          <div key={entry.provider} className="notice">
            {t('social.notSetUp', { provider: providerName(entry.provider) })}
          </div>
        ))}

      {connections.length === 0 && (
        <div className="empty-state">
          <Icon.link />
          <p>{t('social.empty')}</p>
        </div>
      )}

      {connections.map((connection) => (
        <ConnectionCard
          key={connection.id}
          connection={connection}
          providerName={providerName(connection.provider)}
          mayManage={mayManage}
          onVerify={() => verify.mutate(connection.id)}
          onReconnect={() => begin(connection.provider)}
          onDisconnect={() => {
            if (window.confirm(t('connections.confirmDisconnect'))) disconnect.mutate(connection.id);
          }}
          busy={verify.isPending || disconnect.isPending || connect.isPending || direct.isPending}
          dateTime={fmt.dateTime}
        />
      ))}

      <SocialApps providers={providers} onChanged={refresh} />
      {(me.data?.capabilities ?? []).includes(Capability.PLATFORM_ADMIN) && (
        <SocialNetworksAdmin onChanged={refresh} />
      )}
    </section>
  );
}

function ConnectionCard(props: {
  connection: SocialConnectionRow;
  providerName: string;
  mayManage: boolean;
  busy: boolean;
  dateTime: (value: string) => string;
  onVerify: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
}) {
  const { connection } = props;
  const { t } = useI18n();
  const needsReconnect = connection.status !== 'ACTIVE';

  return (
    <article className="card">
      <header className="card-head">
        <h3>
          {props.providerName} · {connection.displayName}
        </h3>
        <span className={`badge ${connection.status === 'ACTIVE' ? 'ok' : 'warn'}`}>
          {connection.status}
        </span>
      </header>

      <dl className="facts">
        <dt>{t('connections.tokenExpires')}</dt>
        <dd>
          {connection.tokenExpiresAt ? props.dateTime(connection.tokenExpiresAt) : t('connections.noExpiry')}
          {' · '}
          <span className="muted">
            {connection.canRefresh ? t('social.renews') : t('social.reconnectWhenExpired')}
          </span>
        </dd>
        <dt>{t('connections.lastVerified')}</dt>
        <dd>{connection.lastVerifiedAt ? props.dateTime(connection.lastVerifiedAt) : t('common.never')}</dd>
        <dt>{t('social.accounts')}</dt>
        <dd>
          <ul className="plain-list">
            {connection.accounts.map((account) => (
              <li key={account.id} className={account.isActive ? undefined : 'dim'}>
                {account.name}{' '}
                <span className="muted tiny">
                  {t(`social.account.${account.kind}` as TranslationKey)}
                  {account.isActive ? '' : ` · ${t('social.inactive')}`}
                </span>
              </li>
            ))}
          </ul>
        </dd>
      </dl>

      {connection.invalidationReason && <p className="error small">{connection.invalidationReason}</p>}

      <div className="actions">
        <button type="button" onClick={props.onVerify} disabled={props.busy}>
          <Icon.check />
          {t('connections.verify')}
        </button>
        {needsReconnect && props.mayManage && (
          <button type="button" className="primary" onClick={props.onReconnect} disabled={props.busy}>
            {t('social.reconnect')}
          </button>
        )}
        {props.mayManage && (
          <button type="button" className="danger" onClick={props.onDisconnect} disabled={props.busy}>
            {t('connections.disconnect')}
          </button>
        )}
      </div>
    </article>
  );
}

/** Register and maintain the provider apps the connect buttons depend on. */
function SocialApps({
  providers,
  onChanged,
}: {
  providers: SocialProviderInfo[];
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const apps = useQuery({ queryKey: ['social-apps'], queryFn: api.socialApps });
  const mayManage = (me.data?.capabilities ?? []).includes(Capability.SETTINGS_WRITE);
  const isPlatformAdmin = (me.data?.capabilities ?? []).includes(Capability.PLATFORM_ADMIN);

  const [chosen, setChosen] = useState<SocialProviderId>('');
  const provider: SocialProviderId = chosen || providers[0]?.provider || '';
  const info = providers.find((entry) => entry.provider === provider);
  const [label, setLabel] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [settings, setSettings] = useState<Record<string, boolean | string>>({});
  const [shared, setShared] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const done = () => {
    onChanged();
    void apps.refetch();
  };
  const fail = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const create = useMutation({
    mutationFn: () =>
      api.createSocialApp({
        provider,
        scope: shared ? 'PLATFORM' : 'TENANT',
        label: label.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        apiVersion: apiVersion.trim() || undefined,
        settings,
      }),
    onSuccess: () => {
      setLabel('');
      setClientId('');
      setClientSecret('');
      setApiVersion('');
      setSettings({});
      setNotice({ kind: 'ok', text: t('social.apps.saved') });
      done();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (args: { id: string; input: Parameters<typeof api.updateSocialApp>[1] }) =>
      api.updateSocialApp(args.id, args.input),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('social.apps.saved') });
      done();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: api.deleteSocialApp,
    onSuccess: done,
    onError: fail,
  });

  /** A setting's label: a translation when one exists, else the definition's own wording. */
  const settingLabel = (network: string, setting: { name: string; label: string }) => {
    const key = `social.setting.${network}.${setting.name}`;
    const translated = t(key as TranslationKey);
    return translated === key ? setting.label : translated;
  };

  const canSubmit =
    provider !== '' && mayManage && label.trim() && clientId.trim() && clientSecret.trim().length >= 8 && !create.isPending;

  return (
    <div style={{ marginTop: 26 }}>
      <h3>{t('social.apps.title')}</h3>
      <p className="tiny muted">{t('social.apps.hint')}</p>

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {(apps.data?.apps ?? []).map((row) => (
        <AppRow
          key={row.id}
          app={row}
          editable={mayManage && (row.scope === 'TENANT' || isPlatformAdmin)}
          onUpdate={(input) => update.mutate({ id: row.id, input })}
          onDelete={() => {
            if (window.confirm(t('social.apps.confirmDelete'))) remove.mutate(row.id);
          }}
          busy={update.isPending || remove.isPending}
        />
      ))}

      {mayManage && (
        <form
          className="card composer"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <h4>{t('social.apps.add', { provider: info?.displayName ?? provider })}</h4>
          {providers.length > 1 && (
            <>
              <label htmlFor="social-app-network">{t('social.apps.network')}</label>
              <select
                id="social-app-network"
                value={provider}
                onChange={(event) => {
                  setChosen(event.target.value);
                  setSettings({});
                }}
              >
                {providers.map((entry) => (
                  <option key={entry.provider} value={entry.provider}>
                    {entry.displayName}
                  </option>
                ))}
              </select>
            </>
          )}
          {info?.authType !== 'token' && (
            <p className="tiny muted">
              {t('social.apps.redirect')}:{' '}
              <code className="ltr">{info?.redirectUri}</code>
            </p>
          )}

          <label htmlFor="social-app-label">{t('social.apps.label')}</label>
          <input id="social-app-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />

          <label htmlFor="social-app-client-id">{info?.clientIdLabel ?? t('social.apps.clientId')}</label>
          <input
            id="social-app-client-id"
            className="ltr"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
          />

          <label htmlFor="social-app-secret">{info?.clientSecretLabel ?? t('social.apps.clientSecret')}</label>
          <input
            id="social-app-secret"
            className="ltr"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="new-password"
          />

          <label htmlFor="social-app-version">{t('social.apps.apiVersion')}</label>
          <input
            id="social-app-version"
            className="ltr"
            value={apiVersion}
            onChange={(e) => setApiVersion(e.target.value)}
            placeholder={info?.defaultApiVersion ?? ''}
            maxLength={40}
          />

          {(info?.settings ?? []).map((setting) =>
            setting.type === 'boolean' ? (
              <label key={setting.name} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(settings[setting.name] ?? setting.default)}
                  onChange={(e) => setSettings({ ...settings, [setting.name]: e.target.checked })}
                />
                <span>{settingLabel(provider, setting)}</span>
              </label>
            ) : (
              <div key={setting.name}>
                <label htmlFor={`social-setting-${setting.name}`}>{settingLabel(provider, setting)}</label>
                {setting.options ? (
                  <select
                    id={`social-setting-${setting.name}`}
                    value={String(settings[setting.name] ?? setting.default ?? setting.options[0])}
                    onChange={(e) => setSettings({ ...settings, [setting.name]: e.target.value })}
                  >
                    {setting.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`social-setting-${setting.name}`}
                    value={String(settings[setting.name] ?? '')}
                    placeholder={typeof setting.default === 'string' ? setting.default : ''}
                    onChange={(e) => setSettings({ ...settings, [setting.name]: e.target.value })}
                  />
                )}
              </div>
            ),
          )}

          {isPlatformAdmin && (
            <label className="checkbox-row">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
              <span>{t('social.apps.registerShared')}</span>
            </label>
          )}

          <button type="submit" className="primary" disabled={!canSubmit}>
            {t('social.apps.save')}
          </button>
        </form>
      )}
    </div>
  );
}

function AppRow(props: {
  app: SocialAppRow;
  editable: boolean;
  busy: boolean;
  onUpdate: (input: {
    clientSecret?: string;
    apiVersion?: string;
    status?: 'ACTIVE' | 'DISABLED';
  }) => void;
  onDelete: () => void;
}) {
  const { app } = props;
  const { t } = useI18n();
  const [secret, setSecret] = useState('');
  const [version, setVersion] = useState(app.apiVersion ?? '');

  return (
    <article className="card">
      <header className="card-head">
        <h4>
          {app.label}{' '}
          <span className="muted tiny">
            {app.scope === 'PLATFORM' ? t('social.apps.shared') : t('social.apps.own')}
          </span>
        </h4>
        <span className={`badge ${app.status === 'ACTIVE' ? 'ok' : 'warn'}`}>{app.status}</span>
      </header>
      <dl className="facts">
        <dt>{t('social.apps.clientId')}</dt>
        <dd className="mono small ltr">{app.clientId}</dd>
        <dt>{t('social.apps.clientSecret')}</dt>
        <dd className="small">
          {app.secretFingerprint
            ? t('social.apps.secretStored', { fingerprint: app.secretFingerprint })
            : t('common.no')}
        </dd>
        <dt>{t('social.apps.apiVersion')}</dt>
        <dd className="mono small ltr">{app.apiVersion ?? '—'}</dd>
        <dt>{t('social.apps.redirect')}</dt>
        <dd className="mono tiny ltr">{app.redirectUri}</dd>
        <dd className="tiny muted">{t('social.apps.connections', { count: app.connectionCount })}</dd>
      </dl>

      {props.editable && (
        <div className="actions">
          <input
            type="password"
            className="ltr"
            placeholder={t('social.apps.keepSecret')}
            value={secret}
            autoComplete="new-password"
            onChange={(e) => setSecret(e.target.value)}
          />
          <button
            type="button"
            disabled={props.busy || secret.trim().length < 8}
            onClick={() => {
              props.onUpdate({ clientSecret: secret.trim() });
              setSecret('');
            }}
          >
            {t('social.apps.save')}
          </button>
          <input
            className="ltr"
            placeholder={t('social.apps.apiVersion')}
            value={version}
            maxLength={40}
            onChange={(e) => setVersion(e.target.value)}
          />
          <button
            type="button"
            disabled={props.busy || version.trim() === (app.apiVersion ?? '')}
            onClick={() => props.onUpdate({ apiVersion: version.trim() })}
          >
            {t('social.apps.save')}
          </button>
          <button
            type="button"
            disabled={props.busy}
            onClick={() => props.onUpdate({ status: app.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' })}
          >
            {app.status === 'ACTIVE' ? t('social.apps.disable') : t('social.apps.enable')}
          </button>
          <button type="button" className="danger" disabled={props.busy} onClick={props.onDelete}>
            {t('social.apps.delete')}
          </button>
        </div>
      )}
    </article>
  );
}
